import {
  ChangeEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button, CircularProgress, MenuItem, TextField, Typography,
} from '@material-ui/core';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import FavoriteIcon from '@material-ui/icons/Favorite';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import DonorOrganisationCard from 'src/components/Elements/Donor/DonorOrganisationCard';
import SummaryCard from 'src/components/UI/SummaryCard';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { COLORS } from 'src/constants/Color';
import routes, { Route } from 'src/constants/routes';
import { t } from 'src/lib/language';
import { DonorDashboardData, DonorOrganisationResult } from 'src/models/DonorDashboardModel';
import { setAlert } from 'src/redux/actions/alertActions';
import {
  aggregateDonorOrganisationResults,
  countDonorTaxReceipts,
  downloadDonorTaxReceipts,
  fetchDonorDashboardData,
  fetchDonorOrganisationData,
} from 'src/redux/actions/donorDashboardActions';
import { Store, useAppDispatch } from 'src/redux/Store';

const useStyles = makeStyles(() => ({
  wrapper: {
    marginTop: 24,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 16,
    '@media (max-width: 680px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 8,
    },
  },
  yearLabel: {
    fontFamily: 'Qanelas-Bold',
    fontSize: 16,
    fontWeight: 700,
    color: '#1E293B',
  },
  yearField: {
    width: 140,
    '@media (max-width: 680px)': {
      width: '100%',
    },
  },
  downloadAllButton: {
    marginLeft: 'auto',
    '@media (max-width: 680px)': {
      marginLeft: 0,
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    '@media (max-width: 680px)': {
      gap: 8,
    },
  },
  orgGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: 20,
    marginTop: 20,
    '@media (max-width: 680px)': {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  },
  warningBanner: {
    marginTop: 16,
    padding: '8px 12px',
    borderRadius: 8,
    backgroundColor: COLORS.amberLight,
    color: COLORS.amber,
    fontSize: 13,
  },
  section: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: `1px solid ${COLORS.background}`,
    padding: 24,
    marginTop: 20,
    '@media (max-width: 680px)': {
      padding: 16,
    },
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    padding: 48,
  },
  emptyTitle: {
    fontFamily: 'Qanelas-Bold',
    fontSize: 18,
    fontWeight: 700,
    color: '#1E293B',
  },
  emptyText: {
    fontSize: 14,
    color: COLORS.mediumgrey,
    marginTop: 8,
    marginBottom: 16,
  },
}));

const waitBetweenDownloads = (milliseconds: number) => new Promise<void>((resolve) => {
  window.setTimeout(resolve, milliseconds);
});

const downloadReceiptsSequentially = async (
  organisationIds: string[],
  year: number,
  index = 0,
  statuses: number[] = [],
): Promise<number[]> => {
  if (index >= organisationIds.length) {
    return statuses;
  }
  if (index > 0) {
    await waitBetweenDownloads(750);
  }
  let status = 0;
  try {
    const response = await downloadDonorTaxReceipts(organisationIds[index], year);
    status = response.status;
  } catch {
    status = 0;
  }
  return downloadReceiptsSequentially(
    organisationIds,
    year,
    index + 1,
    [...statuses, status],
  );
};

const DonorDashboardPage = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const organisationsUser = useSelector((state: Store) => state.organisationsUser);
  const hasOrganizations = useSelector((state: Store) => state.user.has_organisations);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const yearOptions = useMemo(
    () => Array.from({ length: 11 }, (_, index) => currentYear - index),
    [currentYear],
  );

  const [data, setData] = useState<DonorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const yearRef = useRef(year);

  const organisations = useMemo(
    () => Object.values(organisationsUser)
      .filter((organisation) => !organisation.hide)
      .sort((first, second) => first.name_en.localeCompare(second.name_en)),
    [organisationsUser],
  );

  useEffect(() => {
    yearRef.current = year;
  }, [year]);

  useEffect(() => {
    let cancelled = false;

    if (organisations.length === 0) {
      setData(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    setFatalError(false);

    fetchDonorDashboardData(organisations, year)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFatalError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [organisations, year, reloadToken]);

  const formatCurrency = useCallback((amount: number) => (
    Number.isFinite(amount) ? amount : 0
  ).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-US', {
    style: 'currency',
    currency: 'CAD',
  }), [lang]);

  const handleYearChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
    setYear(Number(event.target.value));
  };

  const handleRetryOrganisation = async (result: DonorOrganisationResult) => {
    const organisation = organisations.find((item) => item.id === result.id);
    if (!organisation || refreshingId) {
      return;
    }
    const requestedYear = year;
    setRefreshingId(result.id);
    try {
      const refreshedResult = await fetchDonorOrganisationData(organisation, requestedYear);
      if (yearRef.current !== requestedYear) {
        return;
      }
      setData((currentData) => {
        if (!currentData) {
          return currentData;
        }
        const updatedOrganisations = currentData.organisations.map((currentOrganisation) => (
          currentOrganisation.id === refreshedResult.id ? refreshedResult : currentOrganisation
        ));
        return aggregateDonorOrganisationResults(updatedOrganisations);
      });
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDownload = async (result: DonorOrganisationResult) => {
    if (downloadingId || downloadingAll) {
      return;
    }

    setDownloadingId(result.id);
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      code: 'messages.download_started',
    }));

    try {
      const count = await countDonorTaxReceipts(result.id, year);
      if (count.ok && count.count === 0) {
        dispatch(setAlert({
          type: ALERT_TYPE.warning,
          code: 'messages.download_notfound',
        }));
        return;
      }

      const response = await downloadDonorTaxReceipts(result.id, year);
      if (response.status === 400) {
        const errorCode = response.message?.split(' - ')[0];
        if (errorCode === '2008') {
          dispatch(setAlert({
            type: ALERT_TYPE.warning,
            code: 'messages.download_notfound',
          }));
          return;
        }
      }
      if (response.status !== 200) {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.download_error',
        }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.download_success',
      }));
    } catch {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.download_error',
      }));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (downloadingAll || downloadingId || organisations.length === 0) {
      return;
    }

    setDownloadingAll(true);
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      message: t(lang, 'pages.donorDashboard.downloadAllStarted'),
    }));

    try {
      const statuses = await downloadReceiptsSequentially(
        organisations.map((organisation) => organisation.id),
        year,
      );
      const downloaded = statuses.filter((status) => status === 200).length;
      const skipped = statuses.length - downloaded;

      if (downloaded === 0) {
        dispatch(setAlert({
          type: ALERT_TYPE.warning,
          message: t(lang, 'pages.donorDashboard.downloadAllNone'),
        }));
      } else if (skipped > 0) {
        dispatch(setAlert({
          type: ALERT_TYPE.warning,
          message: t(lang, 'pages.donorDashboard.downloadAllPartial', {
            downloaded,
            skipped,
          }),
        }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: t(lang, 'pages.donorDashboard.downloadAllDone', {
            count: downloaded,
          }),
        }));
      }
    } catch {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.download_error',
      }));
    } finally {
      setDownloadingAll(false);
    }
  };

  const renderEmptyState = () => (
    <div className={classes.section}>
      <Typography className={classes.emptyTitle}>
        {t(lang, 'pages.donorDashboard.noOrganisations')}
      </Typography>
      <Typography className={classes.emptyText}>
        {t(lang, 'pages.donorDashboard.noOrganisationsHint')}
      </Typography>
      <Button
        component={RouterLink}
        to={routes[Route.ORGANISATIONS].path}
        variant="contained"
        color="primary">
        {t(lang, 'menus.organisations')}
      </Button>
    </div>
  );

  const renderError = () => (
    <div className={classes.section}>
      <Typography className={classes.emptyTitle}>
        {t(lang, 'pages.donorDashboard.loadError')}
      </Typography>
      <div style={{ marginTop: 16 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={() => setReloadToken((token) => token + 1)}>
          {t(lang, 'pages.donorDashboard.retry')}
        </Button>
      </div>
    </div>
  );

  const waitingForOrganisations = organisations.length === 0 && hasOrganizations !== false;

  const renderOrganisations = () => {
    if (loading || waitingForOrganisations) {
      return (
        <div className={classes.center}>
          <CircularProgress size={40} />
        </div>
      );
    }
    if (organisations.length === 0) {
      return renderEmptyState();
    }
    return (
      <div className={classes.orgGrid}>
        {(data?.organisations ?? []).map((result) => (
          <DonorOrganisationCard
            key={result.id}
            result={result}
            year={year}
            lang={lang}
            formatCurrency={formatCurrency}
            downloading={downloadingId === result.id}
            refreshing={refreshingId === result.id}
            disabled={downloadingAll || downloadingId !== null}
            onDownload={() => handleDownload(result)}
            onRetry={() => handleRetryOrganisation(result)} />
        ))}
      </div>
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.headerRow}>
        <Typography className={classes.yearLabel}>
          {t(lang, 'pages.donorDashboard.year')}
        </Typography>
        <TextField
          className={classes.yearField}
          variant="outlined"
          size="small"
          select
          value={year}
          onChange={handleYearChange}
          inputProps={{ 'aria-label': t(lang, 'pages.donorDashboard.year') }}>
          {yearOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <Button
          className={classes.downloadAllButton}
          variant="contained"
          color="primary"
          startIcon={downloadingAll ? <CircularProgress size={16} /> : <GetAppIcon />}
          disabled={downloadingAll || !!downloadingId || loading || organisations.length === 0}
          onClick={handleDownloadAll}>
          {t(lang, 'pages.donorDashboard.downloadAll')}
        </Button>
      </div>

      {fatalError ? renderError() : (
        <>
          <div className={classes.grid}>
            <SummaryCard
              icon={<AttachMoneyIcon />}
              label={t(lang, 'pages.donorDashboard.paidTotal')}
              value={formatCurrency(data?.totals.paidTotal ?? 0)}
              valueColor={COLORS.action}
              subtitle={t(lang, 'pages.donorDashboard.paymentsCount', {
                count: data?.totals.paidCount ?? 0,
              })}
              loading={loading} />
            <SummaryCard
              icon={<FavoriteIcon />}
              label={t(lang, 'pages.donorDashboard.taxDeductible')}
              value={formatCurrency(data?.totals.deductibleTotal ?? 0)}
              valueColor={COLORS.secondary}
              subtitle={t(lang, 'pages.donorDashboard.organisationsIncluded', {
                included: data?.totals.organisationsIncluded ?? 0,
                total: organisations.length,
              })}
              loading={loading} />
          </div>

          {!loading && data?.hasPartialFailures && (
            <div className={classes.warningBanner}>
              {t(lang, 'pages.donorDashboard.partialTotalsWarning')}
            </div>
          )}

          {renderOrganisations()}
        </>
      )}
    </div>
  );
};

export default DonorDashboardPage;
